import { useEffect, useSyncExternalStore } from 'react';
import { crispService } from '@common/services/crisp-service';

// #787 — a review ask must never sit beside a paywall, an error or an open Crisp panel. Those surfaces
// come and go AFTER the ask is already on screen, so "check at render" is not enough: this is a tiny
// external store the ask subscribes to, so it hides when a blocker appears and returns when it clears.
//
// Dunning is handled separately (shouldRenderNudge reads the dunning selector).

export type ReviewAskBlocker = 'paywall' | 'error' | 'crisp';

const active = new Map<ReviewAskBlocker, number>();
const listeners = new Set<() => void>();
// useSyncExternalStore needs a stable snapshot between changes.
let snapshot: readonly ReviewAskBlocker[] = [];

function publish(): void {
  snapshot = Array.from(active.keys());
  listeners.forEach((l) => l());
}

/** Mark a blocker active (ref-counted: two paywalls open = still blocked until both close). Returns the release. */
export function acquireReviewAskBlocker(reason: ReviewAskBlocker): () => void {
  active.set(reason, (active.get(reason) ?? 0) + 1);
  publish();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    const n = (active.get(reason) ?? 1) - 1;
    if (n <= 0) active.delete(reason);
    else active.set(reason, n);
    publish();
  };
}

export function getReviewAskBlockers(): readonly ReviewAskBlocker[] {
  return snapshot;
}

export function subscribeReviewAskBlockers(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** Test seam. */
export function resetReviewAskBlockers(): void {
  active.clear();
  listeners.clear();
  crispWired = false;
  crispRelease = null;
  publish();
}

let crispWired = false;
let crispRelease: (() => void) | null = null;

function setCrisp(open: boolean): void {
  if (open && !crispRelease) crispRelease = acquireReviewAskBlocker('crisp');
  if (!open && crispRelease) { crispRelease(); crispRelease = null; }
}

/** Follow Crisp's open/close events (once per page; the Crisp "on" API has no per-listener "off"). */
function wireCrisp(): void {
  if (crispWired || !crispService.isAvailable) return;
  crispWired = true;
  setCrisp(crispService.isChatOpened());
  crispService.addEventListener('chat:opened', () => setCrisp(true));
  crispService.addEventListener('chat:closed', () => setCrisp(false));
}

/** The blockers currently up (re-renders when one appears or clears). */
export function useReviewAskBlockers(): readonly ReviewAskBlocker[] {
  useEffect(() => { wireCrisp(); }, []);
  return useSyncExternalStore(subscribeReviewAskBlockers, getReviewAskBlockers, getReviewAskBlockers);
}

/** Hold a blocker while `on` is true (e.g. a paywall is mounted). */
export function useReviewAskBlocker(reason: ReviewAskBlocker, on: boolean = true): void {
  useEffect(() => {
    if (!on) return undefined;
    return acquireReviewAskBlocker(reason);
  }, [reason, on]);
}
